import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Card, Box, Skeleton, useTheme } from "@mui/material";
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import DesBtn from "../Reusable/DesBtn";
import OrderFilter from "./OrderFilter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ThresholdViolationsByCameraChart = () => {
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);

  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const theme = useTheme();

  const [filter, setFilter] = useState({
    start_date: null,
    end_date: null,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${baseURL}analytics/fire/threshold-violations-by-camera`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: filter,
        }
      );
      setDataList(data.data || []);
    } catch (err) {
      console.error("Threshold violations error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  // -----------------------------
  // Prepare Data
  // -----------------------------
  const labels = dataList.map((item) => item.camera_name);

  const data = {
    labels,
    datasets: [
      {
        label: "Above Max Threshold",
        data: dataList.map((item) =>
          item.above_max_count > 0 ? item.above_max_count : null
        ),
        backgroundColor: "rgba(231, 76, 60, 0.8)", // Red
      },
      {
        label: "Below Min Threshold",
        data: dataList.map((item) =>
          item.below_min_count > 0 ? item.below_min_count : null
        ),
        backgroundColor: "rgba(52, 152, 219, 0.8)", // Blue
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: theme.palette.text.primary,
        },
      },
      title: {
        display: true,
        text: "Threshold Violations by Camera",
        color: theme.palette.text.primary,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const item = dataList[ctx.dataIndex];

            if (ctx.dataset.label === "Above Max Threshold") {
              if (!item.above_max_count) return null;
              return [
                `Above Max: ${item.above_max_count}`,
                `Last: ${item.last_above_max_time
                  ? new Date(item.last_above_max_time).toLocaleString()
                  : "-"}`,
                `Violation Rate: ${item.violation_rate}%`,
              ];
            }

            if (!item.below_min_count) return null;
            return [
              `Below Min: ${item.below_min_count}`,
              `Last: ${item.last_below_min_time
                ? new Date(item.last_below_min_time).toLocaleString()
                : "-"}`,
              `Violation Rate: ${item.violation_rate}%`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: theme.palette.text.primary },
        grid: { color: theme.palette.divider },
      },
      y: {
        beginAtZero: true,
        ticks: { color: theme.palette.text.primary },
        grid: { color: theme.palette.divider },
        title: {
          display: true,
          text: "Violation Count",
          color: theme.palette.text.primary,
        },
      },
    },
  };

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Filter by Date",
      content: (
        <OrderFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={setFilter}
        />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  return (
    <Card sx={{ p: 2 }}>
      <DesBtn text="Filter" handle={openFilter}>
        <FilterAltOutlinedIcon />
      </DesBtn>

      {loading ? (
        <Skeleton variant="rectangular" height="500px" />
      ) : (
        <Box sx={{ height: "500px" }}>
          <Bar data={data} options={options} />
        </Box>
      )}
    </Card>
  );
};

export default ThresholdViolationsByCameraChart;
